import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { evenement } from "@/lib/pixel";
import { evenementGA } from "@/lib/analytics";
import { useI18n } from "@/i18n/I18nContext";
import LangSwitch from "@/components/LangSwitch";
import ThemeSwitch from "@/components/ThemeSwitch";
import Marque from "@/components/Marque";
import { Button } from "@/components/ui/button";
import { Check, Loader2, Mail, Phone } from "lucide-react";

const CONTACT = "quotes@roofborder.com";
// Ligne de DÉMONSTRATION de la réceptionniste : c'est l'agent vocal d'un vrai
// couvreur. Celui qui vient de payer peut l'appeler en se faisant passer pour
// son propre client, juste avant de répondre oui ou non.
const TEL_DEMO = "+1 450-328-8648";
const TEL_DEMO_LIEN = "tel:+14503288648";

// Réceptionniste IA — proposée UNIQUEMENT ici, une fois le forfait payé
// (décision de Jeff, 2026-09-24) : elle ne figure plus sur la page des tarifs.
// Réservée aux forfaits mensuels ; À la Carte n'a pas de quoi l'accrocher.
const ADDON_PLANS = ["solo", "team"];
// Le prix vient du serveur (`etat.addon_offre`) : il dépend de la période et de
// la devise du client, et il doit correspondre au centime près à ce que Stripe
// prélève. Aucun montant n'est écrit en dur ici.

/** Retour de paiement Stripe : le compte s'ouvre ici, pas dans un courriel. */
export default function Bienvenue() {
  const { lang } = useI18n();
  const fr = lang !== "en";
  const [etat, setEtat] = useState(null);      // null = en cours
  const [echec, setEchec] = useState(false);
  // null = la question n'a pas encore ete posee/repondue. Tant qu'elle
  // vaut null sur un forfait eligible, la confirmation reste cachee.
  const [addon, setAddon] = useState(null);
  const [addonEnCours, setAddonEnCours] = useState(false);
  // Un « oui » peut etre enregistre sans avoir pu etre facture (carte
  // refusee) : on ne confirme alors surtout pas un achat.
  const [addonFacture, setAddonFacture] = useState(false);
  const essais = useRef(0);

  useEffect(() => {
    const sid = new URLSearchParams(window.location.search).get("session_id") || "";
    if (!sid) { setEchec(true); return; }
    let vivant = true;
    let minuteur;

    // Le compte est ouvert par la page ou par le webhook, selon lequel arrive
    // en premier. On interroge jusqu'à ce que ce soit fait.
    const verifier = async () => {
      try {
        const { data } = await api.get("/public/checkout-status", { params: { session_id: sid } });
        if (!vivant) return;
        if (data?.pret) {
          setEtat(data);
          // Reponse deja donnee (page rechargee, lien rouvert depuis le
          // courriel) : on ne repose pas la question.
          if (data.addon !== null && data.addon !== undefined) {
            setAddon(!!data.addon);
            setAddonFacture(!!data.addon_facture);
          }
          // Compte réellement ouvert et payé : c'est LA vente. Le serveur envoie
          // le même achat par l'API Conversions ; l'identifiant partagé
          // (`purchase_<session Stripe>`) permet à Meta de n'en compter qu'un.
          evenement("Purchase", {
            content_name: data.plan || "", value: data.montant || 0,
            currency: (data.devise || "cad").toUpperCase(),
          }, `purchase_${sid}`);
          // Le MÊME achat pour Google. Sans cet évènement, Google Ads n'a
          // aucune conversion à importer et optimise à l'aveugle. `transaction_id`
          // porte la session Stripe : GA4 dédoublonne dessus si la page est
          // rechargée ou rouverte depuis le courriel de confirmation.
          evenementGA("purchase", {
            transaction_id: sid,
            value: data.montant || 0,
            currency: (data.devise || "cad").toUpperCase(),
            items: [{ item_id: data.plan || "carte", item_name: data.plan || "carte" }],
          });
          return;
        }
      } catch {
        if (!vivant) return;
      }
      essais.current += 1;
      if (essais.current > 20) { setEchec(true); return; }
      minuteur = setTimeout(verifier, 1500);
    };
    verifier();
    return () => { vivant = false; clearTimeout(minuteur); };
  }, []);

  const repondreAddon = async (veut) => {
    if (addonEnCours) return;
    setAddonEnCours(true);
    const sid = new URLSearchParams(window.location.search).get("session_id") || "";
    try {
      const { data } = await api.post("/public/addon-receptionniste",
                                      { session_id: sid, veut });
      setAddonFacture(!!data?.facture);
    } catch {
      // Le choix ne doit JAMAIS bloquer l'acces au compte que le couvreur
      // vient de payer : si l'appel echoue, on avance quand meme, sans
      // pretendre qu'un achat a eu lieu.
      setAddonFacture(false);
    }
    setAddon(veut);
    setAddonEnCours(false);
  };

  // La question n'est posee qu'aux forfaits a abonnement, et une seule fois.
  const addonAPoser = !!etat && ADDON_PLANS.includes(etat.plan) && addon === null;
  const offre = etat?.addon_offre;

  return (
    <div className="min-h-screen bg-app text-ink">
      <header className="border-b border-line">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Marque taille="md" />
          <div className="flex items-center gap-3">
            <ThemeSwitch />
            <LangSwitch />
          </div>
        </div>
      </header>

      <main className="max-w-xl mx-auto px-6 py-20">
        {!etat && !echec && (
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin text-brand mx-auto mb-5" />
            <p className="text-ink-soft">
              {fr ? "Nous confirmons votre paiement et ouvrons votre compte…" : "Confirming your payment and opening your account…"}
            </p>
          </div>
        )}

        {echec && (
          <div className="text-center">
            <h1 className="font-heading text-2xl font-extrabold tracking-tighter mb-3">
              {fr ? "Paiement reçu, compte en cours d'ouverture" : "Payment received, account being opened"}
            </h1>
            <p className="text-ink-soft">
              {fr
                ? "Votre paiement est bien passé, mais l'ouverture du compte prend plus de temps que prévu. Vos identifiants arrivent par courriel dans quelques minutes."
                : "Your payment went through, but opening the account is taking longer than expected. Your credentials will arrive by email within a few minutes."}
            </p>
            <a href={`mailto:${CONTACT}`} className="text-brand-ink hover:underline text-sm mt-4 inline-block">
              {CONTACT}
            </a>
          </div>
        )}

        {/* Porte obligatoire : le forfait est paye, le compte est ouvert, mais
            le couvreur doit trancher sur la receptionniste avant de voir sa
            confirmation. Deux boutons, aucune croix pour fermer — c'est le seul
            moment ou il est sur, attentif, et ou la carte est deja au dossier. */}
        {addonAPoser && (
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-ink mb-3">
              {fr ? "Dernière étape" : "One last step"}
            </p>
            <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tighter leading-tight">
              {fr
                ? "Voulez-vous que l'IA réponde aussi à vos appels ?"
                : "Do you also want AI to answer your calls?"}
            </h1>
            <p className="mt-4 text-ink-soft">
              {fr
                ? "Votre forfait est payé et votre compte est ouvert. Reste une question : une voix qui répond 24/7, qualifie vos clients et prend les demandes de soumission — même quand vous êtes sur un toit."
                : "Your plan is paid and your account is open. One question left: a voice that answers 24/7, qualifies your clients and takes quote requests — even when you are up on a roof."}
            </p>

            <div className="mt-6 rounded-xl border border-brand/40 bg-brand/[0.06] p-6">
              <div className="flex items-center gap-2 mb-4 flex-wrap">
                <Phone className="h-5 w-5 text-brand-ink" />
                <h2 className="font-heading font-extrabold text-lg tracking-tight">
                  Roofer AI Receptionist
                </h2>
              </div>
              <div className="flex flex-wrap items-end gap-x-8 gap-y-3">
                <div>
                  {/* À l'année, c'est le montant RÉELLEMENT prélevé qui prime :
                      le laisser découvrir sur le relevé serait la pire façon de
                      l'apprendre. Le mensuel équivalent suit, entre parenthèses. */}
                  <span className="font-heading font-extrabold text-3xl tracking-tighter">
                    {(offre?.an ?? offre?.mois)} $
                  </span>
                  <span className="text-sm text-ink-faint">
                    {" / "}{offre?.an ? (fr ? "an" : "year") : (fr ? "mois" : "mo")}
                  </span>
                  {offre?.an && (
                    <span className="text-sm text-ink-faint">
                      {" "}({offre.mois} $ / {fr ? "mois" : "mo"})
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 text-sm font-semibold text-emerald-600">
                  <Check className="h-4 w-4 shrink-0" />
                  {fr ? "Aucuns frais de programmation" : "No programming fee"}
                </div>
              </div>
              {offre?.an && (
                <p className="text-sm font-semibold text-emerald-600 mt-2">
                  {fr
                    ? `Tarif annuel : 20 % de moins que ${offre.mois_plein} $ par mois.`
                    : `Yearly rate: 20% less than $${offre.mois_plein} a month.`}
                </p>
              )}
              <p className="text-xs text-ink-faint mt-3">
                {fr
                  ? "200 minutes par mois comprises, puis 0,17 $/min. Annulable en tout temps."
                  : "200 minutes a month included, then $0.17/min. Cancel any time."}
              </p>

              {/* Ecouter tranche mieux que lire : la ligne appelee est l'agent
                  vocal d'un vrai couvreur. */}
              <a href={TEL_DEMO_LIEN} className="mt-5 block">
                <Button variant="outline" className="w-full h-auto py-2.5 flex flex-col gap-0.5 border-brand/50 rounded-md hover:bg-brand/10 hover:text-ink">
                  <span className="text-xs font-semibold">
                    {fr ? "Écoutez-la d'abord — appelez" : "Hear it first — call"}
                  </span>
                  <span className="font-heading font-bold text-brand-ink text-base">{TEL_DEMO}</span>
                </Button>
              </a>
            </div>

            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Button
                onClick={() => repondreAddon(true)}
                disabled={addonEnCours}
                className="h-12 bg-brand hover:bg-brand/90 text-white rounded-md font-bold"
              >
                {addonEnCours && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {fr ? "Oui, ajoutez-la" : "Yes, add it"}
              </Button>
              <Button
                onClick={() => repondreAddon(false)}
                disabled={addonEnCours}
                variant="outline"
                className="h-12 border-line-strong rounded-md font-semibold hover:bg-hover-strong hover:text-ink"
              >
                {fr ? "Non, je m'en passe" : "No, I'll pass"}
              </Button>
            </div>
            <p className="text-xs text-ink-faint mt-4">
              {fr
                ? `En disant oui, ${offre?.an ? `${offre.an} $ sont prélevés maintenant pour l'année` : "la réceptionniste est ajoutée à votre abonnement"} — même carte que votre forfait, rien à ressaisir. Nous vous contactons ensuite pour la configurer avec vos heures et vos mots.`
                : `Saying yes ${offre?.an ? `charges $${offre.an} now for the year` : "adds the receptionist to your subscription"} — same card as your plan, nothing to re-enter. We then contact you to set it up with your hours and your words.`}
            </p>
          </div>
        )}

        {etat && !addonAPoser && (
          <div>
            <div className="h-12 w-12 rounded-full bg-brand/15 flex items-center justify-center mb-6">
              <Check className="h-6 w-6 text-brand" />
            </div>
            <h1 className="font-heading text-3xl font-extrabold tracking-tighter leading-tight">
              {fr ? "Bienvenue chez Roof Border." : "Welcome to Roof Border."}
            </h1>
            <p className="mt-4 text-ink-soft">
              {fr
                ? `Votre forfait ${etat.plan_nom} est actif. Votre compte est ouvert au nom de `
                : `Your ${etat.plan_nom_en} plan is active. Your account is open under `}
              <strong className="text-ink">{etat.email}</strong>.
            </p>

            {etat.activation_url ? (
              <>
                <p className="mt-6 text-ink-soft">
                  {fr
                    ? "Il ne reste qu'à choisir votre mot de passe."
                    : "All that's left is to choose your password."}
                </p>
                <a href={etat.activation_url}>
                  <Button className="mt-5 bg-brand hover:bg-brand/90 text-white rounded-md h-12 px-7 text-base font-bold">
                    {fr ? "Choisir mon mot de passe" : "Choose my password"}
                  </Button>
                </a>
                <p className="mt-4 text-sm text-ink-faint flex items-start gap-2">
                  <Mail className="h-4 w-4 shrink-0 mt-0.5" />
                  {fr
                    ? "Le même lien vous a été envoyé par courriel — vous pouvez fermer cette page sans crainte."
                    : "The same link was emailed to you — you can safely close this page."}
                </p>
              </>
            ) : (
              <>
                <p className="mt-6 text-ink-soft">
                  {fr
                    ? "Connectez-vous avec votre mot de passe habituel : votre nouveau forfait est déjà appliqué."
                    : "Sign in with your usual password: your new plan is already applied."}
                </p>
                <Link to="/login">
                  <Button className="mt-5 bg-brand hover:bg-brand/90 text-white rounded-md h-12 px-7 text-base font-bold">
                    {fr ? "Se connecter" : "Sign in"}
                  </Button>
                </Link>
              </>
            )}

            {addon !== null && (
              <p className={`mt-8 text-sm text-ink-soft border-l-2 pl-3 ${
                addon && !addonFacture ? "border-amber-500" : "border-brand/50"
              }`}>
                {!addon
                  ? (fr
                      ? "Vous avez décliné la réceptionniste IA. Elle reste disponible plus tard — écrivez-nous quand vous voulez."
                      : "You passed on the AI receptionist. It stays available later — write to us whenever you like.")
                  : addonFacture
                    ? (fr
                        ? "Réceptionniste IA ajoutée à votre abonnement. Nous vous écrivons dans un jour ouvrable pour la configurer avec vos heures et vos mots."
                        : "AI receptionist added to your subscription. We will write to you within one business day to set it up with your hours and your words.")
                    /* Le oui est enregistre mais le paiement n'est pas passe :
                       on ne confirme PAS un achat qui n'a pas eu lieu. */
                    : (fr
                        ? "Réceptionniste IA : votre demande est enregistrée, mais le paiement n'a pas pu être pris sur votre carte. Nous vous contactons pour régler ça — rien n'a été facturé."
                        : "AI receptionist: your request is recorded, but the payment could not be taken from your card. We will contact you to sort it out — nothing was charged.")}
              </p>
            )}

            <div className="mt-12 border-t border-line pt-8">
              <h2 className="font-heading font-bold text-lg tracking-tight mb-3">
                {fr ? "Votre première heure" : "Your first hour"}
              </h2>
              <ol className="space-y-2 text-sm text-ink-soft list-decimal pl-5">
                <li>{fr ? "Entrez vos prix et vos matériaux dans Configuration → Prix et produits." : "Enter your prices and materials under Configuration → Pricing and products."}</li>
                <li>{fr ? "Ajoutez votre logo et vos coordonnées : ils apparaissent sur chaque PDF." : "Add your logo and contact details: they appear on every PDF."}</li>
                <li>{fr ? "Faites une soumission sur une maison que vous connaissez, pour valider vos taux." : "Run a quote on a house you know, to check your rates."}</li>
                <li>{fr ? "Collez l'estimateur sur votre site web pour recevoir des prospects." : "Paste the estimator on your website to start getting leads."}</li>
              </ol>
              <p className="text-sm text-ink-faint mt-6">
                {fr ? "Une question ? Écrivez-nous à " : "Any question? Write to us at "}
                <a href={`mailto:${CONTACT}`} className="text-brand-ink hover:underline">{CONTACT}</a>.
              </p>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
